import React, { useEffect, useRef, useState } from 'react';

export type SnackbarVariant = 'success' | 'error' | 'info';

interface SnackbarProps {
  isDarkMode: boolean;
  open: boolean;
  message: string;
  variant?: SnackbarVariant;
  onClose?: () => void;
}

const Snackbar: React.FC<SnackbarProps> = ({ isDarkMode, open, message, variant = 'info', onClose }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const hideTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (open) {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = undefined;
      }
      setShouldRender(true);
      // Next tick so transitions apply.
      requestAnimationFrame(() => setIsVisible(true));
      return;
    }

    // Start fade-out animation; keep mounted until transition ends.
    setIsVisible(false);
    if (shouldRender) {
      hideTimerRef.current = window.setTimeout(() => {
        setShouldRender(false);
        hideTimerRef.current = undefined;
      }, 220);
    }
  }, [open, shouldRender]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (!shouldRender) return null;

  const bg =
    variant === 'success'
      ? '#16A34A'
      : variant === 'error'
        ? '#B91C1C'
        : (isDarkMode ? '#0F172A' : '#111827');

  const text = '#ffffff';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        zIndex: 20000,
        maxWidth: 'min(760px, calc(100vw - 32px))',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          backgroundColor: bg,
          color: text,
          padding: '6px 12px',
          borderRadius: 4,
          boxShadow: '0 10px 20px rgba(0,0,0,0.25)',
          fontWeight: 800,
          fontSize: 12,
          letterSpacing: 0.2,
          pointerEvents: 'auto',
          userSelect: 'none',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(6px)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      >
        <span style={{ opacity: 0.95 }}>{message}</span>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 900,
              fontSize: 14,
              lineHeight: 1,
              padding: 4,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default Snackbar;
